/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react'
import { IconButton, Box, Typography } from '@mui/material'
import { FetchAutoComplete } from '~/components/FetchAutoComplete'
import {
  getAutoCompleteOptions,
  getPlaceDetails,
  LocationPrediction as BaseLocationPrediction,
} from '~/utils/location'

// Estendendo o tipo para incluir propriedades adicionais
type LocationPrediction = BaseLocationPrediction & {
  readonly from_interest_point?: boolean
  readonly priority?: string
}

type GroupParams = {
  readonly key: string
  readonly group: string
  readonly children: React.ReactNode
}
import { LatLng } from 'leaflet'
import { useMap } from 'react-leaflet'
import { useHookstate } from '@hookstate/core'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { useLang } from '~/hooks/useLang'

// Classes usadas pelo Leaflet para posicionar controles
const POSITION_CLASSES = {
  bottomleft: 'leaflet-bottom leaflet-left',
  bottomright: 'leaflet-bottom leaflet-right',
  topleft: 'leaflet-top leaflet-left',
  topright: 'leaflet-top leaflet-right',
}

interface LocalizeAddressControlProps {
  readonly position?: 'bottomleft' | 'bottomright' | 'topleft' | 'topright'
}

export default function LocalizeAddressControl({
  position = 'topright',
}: LocalizeAddressControlProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedLatLng, setSelectedLatLng] = useState<{
    readonly lat: number
    readonly lng: number
  } | null>(null)
  const localTextValue = useHookstate<string | null>('')
  const map = useMap()
  const { translate } = useLang()

  const positionClass = POSITION_CLASSES[position]

  // Fechar quando o valor do input for limpo
  useEffect(() => {
    if (localTextValue.get() === '' || localTextValue.get() === null) {
      setIsExpanded(false)
    }
  }, [localTextValue.get()])

  const handleToggleExpansion = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleMouseEnter = () => {
    if (!isExpanded) {
      setIsExpanded(true)
    }
  }

  const handleMouseLeave = () => {
    // Só fecha no mouse leave se não estiver focado no input
    setTimeout(() => {
      const container = document.querySelector('.locality-control-container')
      const inputElement = container?.querySelector('input')
      if (!inputElement || document.activeElement !== inputElement) {
        setIsExpanded(false)
      }
    }, 100)
  }

  const handleContainerClick = (event: React.MouseEvent) => {
    if (!isExpanded) {
      event.preventDefault()
      setIsExpanded(true)
      // Focar no input após expandir
      setTimeout(() => {
        const container = document.querySelector('.locality-control-container')
        const inputElement = container?.querySelector(
          'input'
        ) as HTMLInputElement
        if (inputElement) {
          inputElement.focus()
        }
      }, 350) // Aguardar a animação de expansão
    }
  }

  const orderBy = (options: readonly LocationPrediction[]) => {
    return options.map((option) => ({
      priority: option.from_interest_point
        ? translate('Pontos de Interesse')
        : translate('Endereços'),
      ...option,
    }))
  }

  return (
    <div className={positionClass}>
      <div className="leaflet-control leaflet-bar">
        <Box
          className="locality-control-container"
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'background.paper', // <-- aqui

            borderRadius: '4px',
            overflow: 'hidden',
            transition: 'width 0.3s ease-in-out',
            width: isExpanded ? '320px' : '40px',
            height: '40px',
            cursor: 'pointer',
            boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleContainerClick}
        >
          {/* Ícone de busca quando colapsado */}
          <IconButton
            size="small"
            onClick={handleToggleExpansion}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              borderRadius: '4px',
              color: '#666',
              display: isExpanded ? 'none' : 'flex',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <SearchIcon />
          </IconButton>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              opacity: isExpanded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <FetchAutoComplete<LocationPrediction>
              fetchFunc={(input) => {
                const mapCenter = map.getCenter()
                const currentLatLng = selectedLatLng
                  ? new LatLng(selectedLatLng.lat, selectedLatLng.lng)
                  : new LatLng(mapCenter.lat, mapCenter.lng)

                return getAutoCompleteOptions(input, {
                  latLng: currentLatLng,
                })
              }}
              setFunc={async (opt) => {
                let lat = opt?.latitude
                let lng = opt?.longitude
                // Se não for ponto de interesse e não tem lat/lng, buscar detalhes pelo placeId
                if (
                  !opt?.from_interest_point &&
                  (!lat || !lng) &&
                  opt?.placeId
                ) {
                  try {
                    const details = await getPlaceDetails(opt.placeId)
                    lat = details.latitude
                    lng = details.longitude
                  } catch (err) {
                    // fallback: não faz nada
                  }
                }
                if (lat && lng) {
                  setSelectedLatLng({ lat, lng })
                  if (map) {
                    map.setView([lat, lng], 16, {
                      animate: true,
                      duration: 1,
                    })
                  }
                }
                return Promise.resolve({
                  ended: false,
                  value: opt?.description || '',
                })
              }}
              setValueInput={(opt) => opt?.description || ''}
              stateValue={localTextValue}
              noOptionsString="No Locations, yet..."
              optionKey="description"
              autoCompleteProps={{
                getOptionLabel: (item) => item?.description || '',
                placeholder: 'Localização',
                size: 'small',
                errorMsg: false,
                filterOptions: (original) => orderBy(original),
                groupBy: (option: LocationPrediction) => option.priority,
                renderGroup: (params: GroupParams) => (
                  <div key={params.key}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      {params.group}
                    </Typography>
                    {params.children}
                  </div>
                ),
              }}
              sx={{
                width: '100%',
                color: 'text.primary',
              }}
              isValid
            />
          </Box>
        </Box>
      </div>
    </div>
  )
}
